import type {MenuOption} from '@spaced-out/ui-design-system';

import {
  RECURRENCE_FREQUENCY_SCHEMA,
  RECURRENCE_MODE,
  RECURRENCE_EXPIRY_TYPE,
  MEETING_LOCATION,
} from './constants';
import {camel, snake} from 'src/utils';
import {omit} from 'lodash';
import type {SmartSchedulerSchemaType} from './schema';
import type {
  SmartScheduler,
  SmartSchedulerApi,
} from 'src/types/scheduler/smartScheduler';
import type {TranslationFunction} from 'src/types/language-support';
import {
  parseNodeSchedule,
  normalizeNodeSchedule,
} from 'src/utils/node-schedule';

//'months', 'years', 'days', 'weeks'
export const getRepeatCountOptions = (): MenuOption[] => {
  return Array.from({length: 10}, (_, index) => ({
    label: (index + 1).toString(),
    key: (index + 1).toString(),
  }));
};

export const getRepeatUnitOptions = (t: TranslationFunction): MenuOption[] => {
  const frequencyValues = RECURRENCE_FREQUENCY_SCHEMA.options;

  const labelMap: Record<string, string> = {
    MONTHLY: t('Month', 'Month'),
    YEARLY: t('Year', 'Year'),
    DAILY: t('Day', 'Day'),
    WEEKLY: t('Week', 'Week'),
  };

  return frequencyValues.map((value) => ({
    label: labelMap[value] || value,
    key: value,
  }));
};

export const getRepeatOnOptions = (t: TranslationFunction): MenuOption[] => {
  return [
    {
      label: t('Same_Day', 'Same Day'),
      key: RECURRENCE_MODE.SAME_DAY,
    },
    {
      label: t('Same_Date', 'Same Date'),
      key: RECURRENCE_MODE.SAME_DATE,
    },
  ];
};

export const getRepeatUntilOptions = (t: TranslationFunction): MenuOption[] => {
  return [
    {
      label: t('Indefinitely', 'Indefinitely'),
      key: RECURRENCE_EXPIRY_TYPE.INFINITE,
    },
    {
      label: t('Specific_Date', 'Specific Date'),
      key: RECURRENCE_EXPIRY_TYPE.UNTIL,
    },
    {
      label: t('Repeat_Limit', 'Repeat Limit'),
      key: RECURRENCE_EXPIRY_TYPE.COUNT,
    },
  ];
};

export const getMeetingDurationOptions = (
  t: TranslationFunction,
): MenuOption[] => {
  return [30, 60, 90, 120].map((minutes) => {
    return {key: `${minutes}`, label: `${minutes} ${t('minutes', 'minutes')}`};
  });
};

export const getMeetingLocationOptions = (
  t: TranslationFunction,
): MenuOption[] => {
  return [
    {
      label: t('Zoom', 'Zoom'),
      key: MEETING_LOCATION.ZOOM,
    },
    {
      label: t('Google_Meet', 'Google Meet'),
      key: MEETING_LOCATION.GOOGLE_MEET,
    },
    {
      label: t('Microsoft_Teams', 'Microsoft Teams'),
      key: MEETING_LOCATION.MICROSOFT_TEAMS,
    },
    {
      label: t('Custom_Location', 'Custom Location'),
      key: MEETING_LOCATION.CUSTOM_LOCATION,
    },
  ];
};

export const getDefaultSmartSchedulerFormData =
  (): SmartSchedulerSchemaType => {
    return {
      dateSource: 'transcript' as const,
      dateValue: '',
      dateValueAttribute: '',
      meetingDuration: '30',
      meetingLocation: 'custom_location' as const,
      meetingTitle: '',
      hostEmail: '',
      attendeeEmail: '',
      attendeeName: '',
      attendeePhoneNumber: '',
      candidateInviteMode: [],
      notifyHost: false,
      meetingDescription: '',
      fallbackTemplate: {
        id: '',
        name: '',
      },
    };
  };

export const getSmartSchedulerPayload = (
  node: SmartScheduler,
  data: SmartSchedulerSchemaType,
): SmartSchedulerApi => {
  const nodeScheduleData = normalizeNodeSchedule(node);
  const payload = omit(
    {
      ...data,
      dateValue: data.dateValue ? `${data.dateValue}:00` : null,
      dateValueAttribute: data.dateValueAttribute
        ? data.dateValueAttribute
        : 'manual',
      meetingDuration: {amount: +data.meetingDuration, unit: 'minutes'},
      meetingLocation:
        data.meetingLocation === 'custom_location'
          ? [{type: 'custom_location', meta: {value: data.customLocation}}]
          : [{type: 'meeting_link', meta: {provider: data.meetingLocation}}],
      interviewersInfo: {
        hosts: [data.hostEmail],
        optional: data.interviewers ?? [],
        required: [],
      },
      recurrence: data.recurrence?.repeat
        ? omit(
            {
              ...data.recurrence,
              interval: data.recurrence.interval
                ? +data.recurrence.interval
                : undefined,
            },
            ['repeat'],
          )
        : null,
      fallbackTemplateId: data.fallbackTemplate?.id,
      fallbackTemplateName: data.fallbackTemplate?.name,
    },
    [
      'customLocation',
      'hostEmail',
      'fallbackTemplate',
      'dateSource',
      'interviewers',
    ],
  );

  return snake({
    ...nodeScheduleData,
    task: {dateSource: data.dateSource, params: payload},
  });
};

export const parseSmartSchedulerNode = (
  node: SmartSchedulerApi,
): SmartScheduler => {
  const task = camel(node.task);
  const data = task.params;
  // Remove :00 milliseconds if dateSource is manual

  const smartSchedulerNode = {
    ...data,
    dateSource: task.dateSource,
    meetingDuration: `${data.meetingDuration.amount}`,
    dateValue: data.meetingDatetime ? data.meetingDatetime.slice(0, -3) : '',
    dateValueAttribute: task.dateValueAttribute ? task.dateValueAttribute : '',
    meetingLocation:
      data.meetingLocation[0].type === 'custom_location'
        ? 'custom_location'
        : data.meetingLocation[0].meta.provider,
    interviewers: data.interviewersInfo.optional,
    hostEmail: data.interviewersInfo.hosts[0],
    recurrence: data.recurrence
      ? {
          repeat: true,
          ...data.recurrence,
          interval: data.recurrence.interval
            ? `${data.recurrence.interval}`
            : undefined,
          // Migrate old SPECIFIC_WEEK_DAY to SAME_DATE
          mode:
            data.recurrence.mode === 'SPECIFIC_WEEK_DAY'
              ? 'SAME_DATE'
              : data.recurrence.mode,
        }
      : {repeat: false},
    fallbackTemplate: {
      id: data.fallbackTemplateId,
      name: data.fallbackTemplateName,
    },
    attendeePhoneNumber: data.attendeePhoneNumber || data.attendeePhone,
    customLocation:
      data.meetingLocation[0].type === 'custom_location'
        ? data.meetingLocation[0].meta.value
        : undefined,
  };

  const scheduleData = parseNodeSchedule(node);

  return {...scheduleData, task: {params: smartSchedulerNode}};
};
